import React, { useEffect, useState } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel, Alert, Chip, Avatar,
  Typography, Divider, Grid, FormGroup, Checkbox, Paper, Tooltip
} from '@mui/material';
import { Add, Edit, PersonOff, AdminPanelSettings } from '@mui/icons-material';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

const MODULES = [
  { key: 'dashboard',      label: 'Dashboard',             description: 'View KPIs and charts' },
  { key: 'beneficiaries',  label: 'Beneficiaries',         description: 'Manage supplier/beneficiary master' },
  { key: 'invoices',       label: 'Invoices',              description: 'Create and manage purchase invoices' },
  { key: 'forex_deals',    label: 'Forex Deals',           description: 'Track Kotak forex deal bookings' },
  { key: 'remittances',    label: 'Outward Remittances',   description: 'Manage outward remittance transactions' },
  { key: 'boe',            label: 'BOE Knock-off',         description: 'Bill of Entry management & knock-off' },
  { key: 'users',          label: 'User Management',       description: 'Admin-only: manage users & roles' },
];

const ROLE_DEFAULTS = {
  admin:   { dashboard: true, beneficiaries: true, invoices: true, forex_deals: true, remittances: true, boe: true, users: true },
  manager: { dashboard: true, beneficiaries: true, invoices: true, forex_deals: true, remittances: true, boe: true, users: false },
  user:    { dashboard: true, beneficiaries: false, invoices: true, forex_deals: false, remittances: false, boe: false, users: false },
};

const EMPTY_FORM = { name: '', email: '', password: '', role_id: 3, is_active: true, module_permissions: null };

function ModulePermissions({ roleName, value, onChange }) {
  const defaults = ROLE_DEFAULTS[roleName] || ROLE_DEFAULTS.user;
  const perms = value || defaults;

  const handleToggle = (key) => {
    const updated = { ...perms, [key]: !perms[key] };
    onChange(updated);
  };

  const resetToDefaults = () => onChange(null);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2">Module Access</Typography>
        <Button size="small" onClick={resetToDefaults} variant="text" color="secondary">
          Reset to role defaults
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Customise which modules this user can access (overrides role defaults)
      </Typography>
      <FormGroup>
        <Grid container spacing={0.5}>
          {MODULES.map((mod) => (
            <Grid item xs={6} key={mod.key}>
              <Tooltip title={mod.description} placement="right">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={perms[mod.key] || false}
                      onChange={() => handleToggle(mod.key)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{mod.label}</Typography>
                    </Box>
                  }
                  sx={{ ml: 0, alignItems: 'flex-start' }}
                />
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </FormGroup>
    </Paper>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [u, r] = await Promise.all([api.get('/users'), api.get('/users/roles')]);
    setUsers(u.data.data);
    setRoles(r.data.data);
  };
  useEffect(() => { load(); }, []);

  const getRoleName = (id) => roles.find(r => r.id === id)?.name || 'user';

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setDialog(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    const perms = u.module_permissions
      ? (typeof u.module_permissions === 'string' ? JSON.parse(u.module_permissions) : u.module_permissions)
      : null;
    setForm({
      name: u.name, email: u.email, password: '',
      role_id: u.role_id, is_active: u.is_active,
      module_permissions: perms
    });
    setError('');
    setDialog(true);
  };

  const handleRoleChange = (roleId) => {
    const roleName = roles.find(r => r.id === parseInt(roleId))?.name || 'user';
    setForm(f => ({ ...f, role_id: roleId, module_permissions: null })); // reset overrides on role change
  };

  const handleSave = async () => {
    if (!form.name || !form.email) { setError('Name and email required'); return; }
    if (!editing && !form.password) { setError('Password required for new user'); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) await api.put(`/users/${editing.id}`, payload);
      else await api.post('/users', payload);
      setDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving user');
    } finally { setLoading(false); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const getAccessSummary = (u) => {
    const perms = u.module_permissions
      ? (typeof u.module_permissions === 'string' ? JSON.parse(u.module_permissions) : u.module_permissions)
      : ROLE_DEFAULTS[u.role_name] || ROLE_DEFAULTS.user;
    const count = Object.values(perms).filter(Boolean).length;
    return `${count}/${MODULES.length} modules`;
  };

  return (
    <Box>
      <PageHeader
        title="User Management"
        subtitle="Manage system users, roles, and individual module access"
        action={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add User</Button>}
      />

      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Module Access</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: u.role_name === 'admin' ? '#d32f2f' : u.role_name === 'manager' ? '#ed6c02' : 'primary.main', fontSize: 13 }}>
                      {u.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{u.email}</TableCell>
                <TableCell>
                  <Chip
                    label={u.role_name}
                    size="small"
                    icon={u.role_name === 'admin' ? <AdminPanelSettings fontSize="small" /> : undefined}
                    color={u.role_name === 'admin' ? 'error' : u.role_name === 'manager' ? 'warning' : 'default'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getAccessSummary(u)}
                    size="small"
                    variant="outlined"
                    color={u.module_permissions ? 'secondary' : 'default'}
                  />
                  {u.module_permissions && (
                    <Typography variant="caption" color="secondary" sx={{ display: 'block', fontSize: 10 }}>
                      Custom permissions
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={u.is_active ? 'Active' : 'Inactive'} size="small" color={u.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {u.last_login ? new Date(u.last_login).toLocaleString('en-IN') : 'Never'}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEdit(u)} title="Edit"><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDeactivate(u.id)} title="Deactivate" sx={{ color: 'error.main' }}>
                    <PersonOff fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialog} onClose={(_, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setDialog(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1.5 }}>Account Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Full Name" fullWidth required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email Address" type="email" fullWidth required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label={editing ? 'New Password (leave blank to keep)' : 'Password *'}
                type="password" fullWidth value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Role" select fullWidth value={form.role_id}
                onChange={(e) => handleRoleChange(e.target.value)}>
                {roles.map((r) => (
                  <MenuItem key={r.id} value={r.id} sx={{ textTransform: 'capitalize' }}>
                    {r.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={Boolean(form.is_active)} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />}
                  label="Account Active"
                />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <ModulePermissions
            roleName={getRoleName(parseInt(form.role_id))}
            value={form.module_permissions}
            onChange={(perms) => setForm({ ...form, module_permissions: perms })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {editing ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
